import { useState } from 'react';
import { Alert, Button, PhotoThumb } from '@indipia/ui-core';
import { formatReceivedAt } from '@indipia/shared-features';
import { withDevShareId } from '../../api/worklogApi';
import { PhotoLightbox } from '../PhotoLightbox';
import { sortPhotos } from './custReqReport.logic';
import { CUST_REQ_APPLY_MESSAGE as MSG, doneHintText, sectionTitle } from './custReqApply.logic';
import type { CustReqApplyCtl } from './useCustReqApply';

/**
 * 반장 작업보고 작성(C-1) '고객 요청 n건' 영역 — 현장 사진 바로 아래. 기획 941 S16-10.
 * 요청마다 큰 [반영했어요](누르면 초록 ✓). 체크는 제출 전 폼에서만 풀 수 있다(서버 해제 없음).
 * 반영 대상이 없으면(요청 없음·공정 불확정·작성 불가) 영역을 그리지 않는다.
 */
export function CustReqApplySection({
  ctl, writable, submitting,
}: {
  ctl: CustReqApplyCtl;
  writable: boolean;
  submitting: boolean;
}) {
  const [lb, setLb] = useState<{ photos: { url: string }[]; index: number } | null>(null);
  if (ctl.targets.length === 0) return null;
  const disabled = !writable || submitting;

  return (
    <section className="mt-5" aria-label={sectionTitle(ctl.targets.length)}>
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="text-body-sm font-bold text-text">{sectionTitle(ctl.targets.length)}</span>
      </div>
      <p className="mt-1 text-caption text-text-muted">{MSG.guide}</p>

      <ul className="mt-2 flex flex-col gap-3">
        {ctl.targets.map((t, idx) => {
          const applied = ctl.checks[t.custReqId] === true;
          const reqPhotos = sortPhotos(t.photos);
          const received = formatReceivedAt(t.receivedAt);
          return (
            <li key={t.custReqId} className="rounded-md border border-border p-3">
              <div className="flex items-start gap-2">
                <span className="shrink-0 text-body-sm font-bold text-text">{idx + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-body-sm leading-relaxed text-text" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                    {t.content}
                  </p>
                  <p className="mt-1 text-caption text-text-muted">
                    {[t.srcName, received && `${received} 접수`].filter(Boolean).join(' · ')}
                  </p>
                  {reqPhotos.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {reqPhotos.map((p, i) => (
                        <PhotoThumb
                          key={p.photoId}
                          size="sm"
                          src={withDevShareId(p.thumbUrl)}
                          alt={`${MSG.requestPhoto} ${i + 1}`}
                          onOpen={() => setLb({
                            photos: reqPhotos.map((q) => ({ url: withDevShareId(q.url || q.thumbUrl) as string })),
                            index: i,
                          })}
                        />
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <Button
                type="button"
                size="lg"
                variant={applied ? 'success' : 'secondary'}
                className="mt-3 w-full"
                aria-pressed={applied}
                disabled={disabled}
                onClick={() => ctl.setApplied(t.custReqId, !applied)}
              >
                {applied ? `✓ ${MSG.applied}` : MSG.applied}
              </Button>
            </li>
          );
        })}
      </ul>

      <PhotoLightbox
        photos={lb?.photos ?? []}
        index={lb?.index ?? null}
        onClose={() => setLb(null)}
        onIndexChange={(i) => setLb((prev) => (prev ? { ...prev, index: i } : prev))}
      />
    </section>
  );
}

/** '작업완료' 선택 시 미체크 건수 한 줄 안내 — 제출은 막지 않는다. 안내할 것이 없으면 그리지 않는다 */
export function CustReqDoneHint({ ctl, isDone }: { ctl: CustReqApplyCtl; isDone: boolean }) {
  const text = doneHintText(isDone, ctl.unchecked);
  if (!text) return null;
  return (
    <Alert kind="warning" className="mt-2.5" role="status">
      {text}
    </Alert>
  );
}
